/**
 * Rounds — the pure math (docs/ROUNDS.md §4.3; migration 3039 is the other
 * half). DESIGN-CHANGELOG 2026-08-25 "Rounds: nominate with quarters, spin a
 * wheel, on a page of its own" and the entries above it.
 *
 * Everything here is deterministic and takes its inputs explicitly, because
 * the one property the feature rests on is that EVERY client computes the same
 * wheel pose from the same spin record at the same server instant. The server
 * (`round_spin()`) draws the winner and the jitter once and freezes the slice
 * table into the row; nothing in this file is allowed to be random.
 *
 * The constants mirror `public.round_const()` in migration 3039. Change them
 * together — the server stamps `spin_started_at` and the client replays a
 * curve of `R_SPIN_MS`, so a mismatch is a wheel that lands early or late.
 */

import type { User, VoteValue } from './types.ts';
import { isCall, LVL } from './logic.ts';

/** quarters per member per round */
export const R_QUOTA = 3;
/** wheel travel, ms — must match `round_const('spin_ms')` */
export const R_SPIN_MS = 4600;
/** the 5 s auto-disarm on every destructive control (changelog 2026-08-26) */
export const R_ARM_MS = 5000;
/** tickets by tier, weighted mode — `round_const('tk_*')` */
export const RTK: Record<RoundTier, number> = { s: 4, a: 3, b: 2, c: 1 };
/**
 * SECONDING (3054): what one extra nominator is worth, in tickets. The ladder's
 * own rungs are one apart, so a rung IS a ticket and "upgrade its tier" and
 * "+1 ticket" are the same number — mirrors `round_const('tk_second')`.
 */
export const R_SECOND_TK = 1;

export type RoundTier = 'c' | 'b' | 'a' | 's';
const TIER_BY_AVG: readonly RoundTier[] = ['c', 'b', 'a', 's'];
/** Where each tier sits on the ladder. `RTK[t] === TIER_RANK[t] + 1`. */
export const TIER_RANK: Record<RoundTier, number> = { c: 0, b: 1, a: 2, s: 3 };

/** One nomination as the server reports it (`round_json` → `noms[]`). */
export interface RoundNom {
  /** appid */
  id: number;
  /** nominator's user key */
  by: string;
  /** epoch ms */
  at: number;
}

/** One GAME in the hat: every quarter on it, in the order they were spent. */
export interface RoundHeld {
  /** appid */
  id: number;
  /** nominator user keys, earliest first */
  bys: string[];
  /** the EARLIEST nomination — the game's place in draw order */
  at: number;
}

/** One slice of the frozen table the draw used (`spin_geom`). */
export interface RoundGeomRow {
  appid: number;
  /** the first nominator — the slice's hue */
  by: string;
  /** every nominator, earliest first (3054) */
  bys: string[];
  /** the SEATED players' call, before seconding moves it */
  tier: RoundTier;
  w: number;
}

/** The spin record — written once by `round_spin()`, replayed by everyone. */
export interface RoundSpin {
  seq: number;
  from: number;
  to: number;
  /** SERVER epoch ms the wheel starts moving */
  startedAt: number;
  ms: number;
  /** winning appid — in the row from the draw, hidden until the curve lands */
  winner: number;
  geom: RoundGeomRow[];
}

/** The open round, one JSON snapshot from `round_get()`. */
export interface RoundSnapshot {
  id: string;
  crewId: string;
  startedBy: string;
  /** who holds the lever now — `leader_id` as a user key */
  by: string;
  startedAt: number;
  /** the clock the page shows; RESET restamps it */
  at: number;
  lastActivityAt: number;
  wtd: boolean;
  lock: boolean;
  /**
   * The SCHEDULE night this round was started from (3071, `rounds.sched_night`),
   * or null when it was started from the board. BACK and END return there.
   */
  schedNight: string | null;
  /** user keys, join order */
  joined: string[];
  noms: RoundNom[];
  spin: RoundSpin | null;
}

// ---------------------------------------------------------------------------
// tier + tickets
// ---------------------------------------------------------------------------

/**
 * A game's tier IN GAME NIGHT: the rounded average call over every player
 * SEATED IN IT who has called it (S3·A2·B1·C0), no quorum, uncalled = C.
 *
 * `detailVerdict`'s maths over a different roster. `users` is whoever is
 * seated tonight — NOT the crew (Marcus, 2026-08-28: the odds should belong
 * to the people actually playing) and NOT the WHO'S PLAYING selection either,
 * which is the board's control and has nothing to say here. Seats move, so
 * the tiers move with them: a join or a leave re-tiers the wheel and the rail
 * on the next snapshot, with no extra state to keep in step.
 *
 * Mirrors `public.round_seat_tier()`, which is what the draw actually uses —
 * this one only paints before a spin freezes the table.
 */
export function rTier(
  votes: Readonly<Record<string, VoteValue | undefined>> | undefined,
  users: readonly User[],
): RoundTier {
  const calls = users.map((u) => votes?.[u.k]).filter((v): v is VoteValue => !!v && isCall(v));
  if (!calls.length) return 'c';
  const avg = calls.reduce((s, v) => s + LVL[v].v, 0) / calls.length;
  return TIER_BY_AVG[Math.round(avg)] ?? 'c';
}

/**
 * Tickets for one game (3054). Every nominator past the first moves it a rung
 * up the ladder, and THE LADDER IS NOT CAPPED AT S: a third quarter on an S
 * game buys a sixth ticket, because capping would quietly eat it and the wedge
 * a player sees has to be the odds they bought.
 *
 * Flat mode stays flat — with WEIGHT BY TIER off there is no ladder for the
 * upgrade to climb, so every nominated game is one ticket however many
 * quarters are on it. Mirrors `public.round_geom()`.
 */
export function ticketsFor(tier: RoundTier, weighted: boolean, nominators = 1): number {
  if (!weighted) return 1;
  return (RTK[tier] ?? 1) + Math.max(0, nominators - 1) * R_SECOND_TK;
}

/** The rung a game actually draws from: its seat tier, plus one per second. */
export function effRank(tier: RoundTier, nominators = 1): number {
  return (TIER_RANK[tier] ?? 0) + Math.max(0, nominators - 1);
}

/**
 * The rung's name. C·B·A·S for the four the crew can call, then `S+1`, `S+2`…
 * for the ones only seconding can reach — spelled out rather than clamped, so
 * a player can see the quarter they spent in the label as well as the wedge.
 */
export function effTierLabel(rank: number): string {
  if (rank <= 0) return 'C';
  if (rank >= TIER_RANK.s) return rank === TIER_RANK.s ? 'S' : 'S+' + (rank - TIER_RANK.s);
  return (TIER_BY_AVG[rank] ?? 'c').toUpperCase();
}

/**
 * The hat, one entry per GAME (3054). `noms` is one entry per QUARTER — that
 * is what the quota and the roster pips count — so the wheel, which draws one
 * slice per game, folds them here. Draw order is the game's earliest
 * nomination, so seconding never re-orders the wheel under anyone's eyes;
 * mirrors `round_geom()`'s `order by first_at, appid`.
 */
export function groupNoms(noms: readonly RoundNom[]): RoundHeld[] {
  const byId = new Map<number, RoundHeld>();
  for (const n of [...noms].sort((x, y) => x.at - y.at || x.by.localeCompare(y.by))) {
    const held = byId.get(n.id);
    if (held) held.bys.push(n.by);
    else byId.set(n.id, { id: n.id, bys: [n.by], at: n.at });
  }
  return [...byId.values()].sort((x, y) => x.at - y.at || x.id - y.id);
}

// ---------------------------------------------------------------------------
// geometry
// ---------------------------------------------------------------------------

export interface Slice<T = number> {
  /** the appid */
  id: T;
  i: number;
  /** degrees clockwise from 12 o'clock */
  a0: number;
  a1: number;
  mid: number;
  span: number;
  w: number;
}

/**
 * Slice table: one entry per nomination in draw order, degrees clockwise from
 * 12 o'clock. `w` is the ticket count the draw uses, so the wedge a player
 * sees IS the odds they get (prototype `rGeom`).
 */
export function rGeom(rows: readonly { id: number; w: number }[]): Slice[] {
  const T = rows.reduce((s, r) => s + r.w, 0) || 1;
  let a = 0;
  return rows.map((r, i) => {
    const span = (r.w / T) * 360;
    const a0 = a;
    a += span;
    return { id: r.id, i, a0, a1: a, mid: a0 + span / 2, span, w: r.w };
  });
}

/** The slice table for a spin record — the frozen one, never recomputed. */
export function spinGeom(spin: RoundSpin): Slice[] {
  return rGeom(spin.geom.map((g) => ({ id: g.appid, w: g.w })));
}

/**
 * cubic-bezier(0.09,0.72,0.04,0.995) solved for y — the wheel's easing
 * (prototype `rBez`). The pose is computed off this curve rather than off the
 * DOM, so what the pin card names is what is actually under the pointer.
 */
export function rBez(t: number): number {
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  const x1 = 0.09,
    x2 = 0.04,
    y1 = 0.72,
    y2 = 0.995;
  const cx = 3 * x1,
    bx = 3 * (x2 - x1) - cx,
    ax = 1 - cx - bx;
  const cy = 3 * y1,
    by = 3 * (y2 - y1) - cy,
    ay = 1 - cy - by;
  let u = t;
  for (let i = 0; i < 6; i++) {
    const x = ((ax * u + bx) * u + cx) * u - t;
    const d = (3 * ax * u + 2 * bx) * u + cx;
    if (Math.abs(d) < 1e-6) break;
    u -= x / d;
  }
  u = Math.min(1, Math.max(0, u));
  return ((ay * u + by) * u + cy) * u;
}

/** Which slice index sits under the 12 o'clock pointer at rotation `rot`. */
export function pinIndexAt(geom: readonly Slice[], rot: number): number {
  if (!geom.length) return -1;
  const at = (((-rot) % 360) + 360) % 360;
  const i = geom.findIndex((x) => at >= x.a0 && at < x.a1);
  return i < 0 ? geom.length - 1 : i;
}

export interface SpinPose {
  /** degrees, clockwise — the SVG transform */
  rot: number;
  /** 0..1 along the curve; 0 before start, 1 once landed */
  progress: number;
  /** the wheel is moving right now */
  spinning: boolean;
  /** the curve has finished — the winner may be shown */
  landed: boolean;
  /** slice index under the pointer, -1 for an empty table */
  pinIndex: number;
  /** the appid under the pointer, 0 for none */
  pinId: number;
}

/**
 * THE SHARED-CLOCK REPLAY. Given the spin record and the current SERVER time,
 * where is the wheel? Every client — the leader's included — renders from this
 * and nothing else, so a late row, a throttled tab or a reload lands on the
 * same pose at the same instant as everyone else.
 */
export function spinPose(spin: RoundSpin, serverNow: number): SpinPose {
  const geom = spinGeom(spin);
  const ms = spin.ms > 0 ? spin.ms : R_SPIN_MS;
  const raw = (serverNow - spin.startedAt) / ms;
  const progress = raw <= 0 ? 0 : raw >= 1 ? 1 : raw;
  const rot = spin.from + (spin.to - spin.from) * rBez(progress);
  const pinIndex = pinIndexAt(geom, rot);
  return {
    rot,
    progress,
    spinning: progress > 0 && progress < 1,
    landed: progress >= 1,
    pinIndex,
    pinId: pinIndex >= 0 ? (geom[pinIndex]?.id ?? 0) : 0,
  };
}

// ---------------------------------------------------------------------------
// state predicates — the same rules the RPCs enforce, for painting
// ---------------------------------------------------------------------------

/** A landed result, or null while nothing has landed (or nothing was spun). */
export function roundWinner(r: RoundSnapshot | null, serverNow: number): number | null {
  if (!r?.spin) return null;
  return spinPose(r.spin, serverNow).landed ? r.spin.winner : null;
}

/** The wheel is moving on every screen right now. */
export function roundSpinning(r: RoundSnapshot | null, serverNow: number): boolean {
  return !!r?.spin && !spinPose(r.spin, serverNow).landed;
}

/**
 * The rail is shut: the lever has been pulled (a spin record exists, in flight
 * or landed) or the pick is locked. Mirrors `public.round_is_shut()`.
 */
export function roundShut(r: RoundSnapshot | null): boolean {
  return !!r && (r.lock || r.spin !== null);
}

/** Quarters `k` has left in round `r`. */
export function quartersLeft(r: RoundSnapshot | null, k: string): number {
  if (!r) return R_QUOTA;
  return Math.max(0, R_QUOTA - r.noms.filter((n) => n.by === k).length);
}

// ---------------------------------------------------------------------------
// wire → snapshot
// ---------------------------------------------------------------------------

/** Parse `round_get()` / any `round_*` RPC's JSON. `null` in → `null` out. */
export function parseRound(j: unknown): RoundSnapshot | null {
  if (!j || typeof j !== 'object') return null;
  const o = j as Record<string, unknown>;
  if (typeof o['id'] !== 'string') return null;
  const ms = (v: unknown): number => (typeof v === 'string' ? Date.parse(v) : typeof v === 'number' ? v : 0);
  const str = (v: unknown): string => (typeof v === 'string' ? v : '');
  const sp = o['spin'];
  let spin: RoundSpin | null = null;
  if (sp && typeof sp === 'object') {
    const s = sp as Record<string, unknown>;
    const geomRaw = Array.isArray(s['geom']) ? (s['geom'] as unknown[]) : [];
    spin = {
      seq: Number(s['seq'] ?? 0),
      from: Number(s['from'] ?? 0),
      to: Number(s['to'] ?? 0),
      startedAt: ms(s['startedAt']),
      ms: Number(s['ms'] ?? R_SPIN_MS),
      winner: Number(s['winner'] ?? 0),
      geom: geomRaw.map((g) => {
        const r = (g ?? {}) as Record<string, unknown>;
        const tier = str(r['tier']);
        const by = str(r['by']);
        // `bys` arrived with 3054. A spin recorded before it (or by a server
        // that has not been migrated yet) still has exactly one nominator, and
        // that is what `by` says — so the old record parses as a hat of one
        // rather than as a slice with nobody on it.
        const bysRaw = Array.isArray(r['bys']) ? (r['bys'] as unknown[]).filter((k): k is string => typeof k === 'string') : [];
        return {
          appid: Number(r['appid'] ?? 0),
          by,
          bys: bysRaw.length ? bysRaw : by ? [by] : [],
          tier: (tier === 's' || tier === 'a' || tier === 'b' ? tier : 'c') as RoundTier,
          w: Number(r['w'] ?? 1),
        };
      }),
    };
  }
  const noms = Array.isArray(o['noms']) ? (o['noms'] as unknown[]) : [];
  const joined = Array.isArray(o['joined']) ? (o['joined'] as unknown[]) : [];
  return {
    id: o['id'] as string,
    crewId: str(o['crewId']),
    startedBy: str(o['startedBy']),
    by: str(o['by']),
    startedAt: ms(o['startedAt']),
    at: ms(o['at']),
    lastActivityAt: ms(o['lastActivityAt']),
    wtd: o['wtd'] !== false,
    lock: o['lock'] === true,
    // 3071. A server that has not been migrated simply never says it.
    schedNight: typeof o['schedNight'] === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(o['schedNight']) ? o['schedNight'] : null,
    joined: joined.filter((k): k is string => typeof k === 'string'),
    noms: noms.map((n) => {
      const r = (n ?? {}) as Record<string, unknown>;
      return { id: Number(r['id'] ?? 0), by: str(r['by']), at: ms(r['at']) };
    }),
    spin,
  };
}
